<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue';
import { IonIcon, IonSpinner } from '@ionic/vue';
import { refreshOutline, cloudOfflineOutline, checkmarkCircleOutline, logInOutline } from 'ionicons/icons';
import { useI18n } from 'vue-i18n';
import type { ConnectionStatus } from '@/utils/connectionLifecycle';

const props = defineProps<{ status: ConnectionStatus }>();
const emit = defineEmits<{ (event: 'retry'): void; (event: 'login'): void }>();
const { locale } = useI18n();
const visible = ref(false);
let timer: ReturnType<typeof setTimeout> | null = null;
const sv = computed(() => locale.value === 'sv');
const text = computed(() => ({
  idle: '',
  checking: sv.value ? 'Kontrollerar anslutningen...' : 'Checking connection...',
  online: sv.value ? 'Ansluten' : 'Connected',
  offline: sv.value ? 'Du är offline. Väntande svar sparas på enheten.' : 'You are offline. Pending answers are kept on this device.',
  unavailable: sv.value ? 'Servern svarar inte just nu. Dina svar finns kvar.' : 'The server is unavailable. Your answers are still here.',
  expired: sv.value ? 'Din inloggning har gått ut. Logga in igen.' : 'Your session has expired. Please sign in again.',
})[props.status]);
watch(() => props.status, (value) => {
  if (timer) clearTimeout(timer);
  visible.value = value !== 'idle' && value !== 'checking';
  if (value === 'checking') timer = setTimeout(() => { visible.value = true; }, 450);
  if (value === 'online') timer = setTimeout(() => { visible.value = false; }, 1800);
}, { immediate: true });
onUnmounted(() => { if (timer) clearTimeout(timer); });
</script>

<template>
  <div v-if="visible" class="connection-status" role="status" aria-live="polite" :data-status="status">
    <ion-spinner v-if="status === 'checking'" name="crescent" aria-hidden="true" />
    <ion-icon v-else :icon="status === 'online' ? checkmarkCircleOutline : cloudOfflineOutline" aria-hidden="true" />
    <span>{{ text }}</span>
    <button v-if="status === 'expired'" type="button" @click="emit('login')" :title="sv ? 'Logga in' : 'Sign in'" :aria-label="sv ? 'Logga in' : 'Sign in'">
      <ion-icon :icon="logInOutline" />
    </button>
    <button v-else-if="status === 'offline' || status === 'unavailable'" type="button" @click="emit('retry')" :title="sv ? 'Försök igen' : 'Try again'" :aria-label="sv ? 'Försök igen' : 'Try again'">
      <ion-icon :icon="refreshOutline" />
    </button>
  </div>
</template>

<style scoped>
.connection-status {
  position: fixed;
  top: calc(env(safe-area-inset-top, 0px) + 8px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 8px;
  width: max-content;
  max-width: min(420px, calc(100% - 32px));
  padding: 8px 12px;
  border: 1px solid #ccd5d1;
  border-radius: 8px;
  background: #fff;
  color: #263b35;
  box-shadow: 0 2px 10px #00000014;
  font-size: 14px;
  line-height: 1.4;
  letter-spacing: 0;
}
.connection-status span { min-width: 0; overflow-wrap: anywhere; }
.connection-status ion-icon, .connection-status ion-spinner { width: 20px; height: 20px; flex-shrink: 0; }
.connection-status button { display: grid; place-items: center; flex-shrink: 0; width: 40px; height: 40px; color: #8d3e79; background: transparent; }
.connection-status[data-status="offline"], .connection-status[data-status="unavailable"] { border-color: #c1a164; }
</style>
